import React, { useEffect, useMemo, useState } from 'react';
import type { Time } from 'lightweight-charts';
import TvChart, { kstEpoch, type Bar, type ChartTheme } from './TvChart';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import { won, pct, bentoLight, bentoDark, bentoSign, type BentoTokens } from '../styles/fdsTheme';

/**
 * 투자 대시보드 — 벤토 레이아웃 샘플 (2026-11-03)
 *
 * 크기가 다른 카드 타일을 12열 그리드에 배치한다 (bergside/awesome-design-skills 'bento', MIT).
 * Behance·Dribbble 투자 대시보드에서 흔한 배치를 따랐다:
 *
 *   ┌ KPI ┬ KPI ┬ KPI ┬ KPI ┐        ← 3열씩, 숫자 하나 + 보조값
 *   ├──── 메인 차트 (8) ────┬ 관심종목 히트맵 (4) ┤
 *   │                      ├ 보유 비중 (4)       ┤
 *   ├ 자산 추이 (4) ┬ 상승률 상위 (4) ┬ 오늘 주문 (4) ┤
 *
 * 라이트/다크 전환 (브라우저에 기억). 데이터는 DB 조회 API 만 쓴다 — KIS 호출·발주 없음.
 */

interface WatchRow { prodId: string; prodNm: string; last?: number; chg?: number; }

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => fmtDate(new Date(Date.now() - n * 86400000));
const arr = (d: any): any[] => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
const eok = (v?: number | null) => (v == null ? '-' : `${(v / 1e8).toLocaleString('ko-KR', { maximumFractionDigits: 2 })}억`);

const THEME_KEY = 'fds_bentoDark';
const readDark = () => { try { return localStorage.getItem(THEME_KEY) === '1'; } catch { return false; } };

const LAYOUT_CSS = `
.bento { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 16px; }
.bento > .t3 { grid-column: span 3; } .bento > .t4 { grid-column: span 4; } .bento > .t8 { grid-column: span 8; grid-row: span 2; }
@media (max-width: 1200px) { .bento > .t3 { grid-column: span 6; } .bento > .t4 { grid-column: span 6; } .bento > .t8 { grid-column: span 12; grid-row: auto; } }
@media (max-width: 720px)  { .bento > .t3, .bento > .t4 { grid-column: span 12; } }
.bento-tile:focus-visible, .bento-btn:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
.bento-row:hover { filter: brightness(0.97); }
`;

const BentoDashboard: React.FC = () => {
  const axios = getMainAxios();
  const { selectedPfId } = useAuth();
  const [dark, setDark] = useState(readDark);
  const t: BentoTokens = dark ? bentoDark : bentoLight;
  const chartTheme: ChartTheme = useMemo(() => ({ bg: t.tileBg, text: t.muted, grid: t.divider, border: t.divider }), [t]);

  const [bs, setBs] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [watch, setWatch] = useState<WatchRow[]>([]);
  const [rise, setRise] = useState<any[]>([]);
  const [prodId, setProdId] = useState('');
  const [period, setPeriod] = useState<'D' | '5m'>('D');
  const [bars, setBars] = useState<Bar[]>([]);
  const [loading, setLoading] = useState(false);

  const toggleDark = () => {
    setDark(d => { try { localStorage.setItem(THEME_KEY, d ? '0' : '1'); } catch { /* 저장 못 해도 동작 */ } return !d; });
  };

  // 계좌 데이터
  useEffect(() => {
    if (!selectedPfId) return;
    const d = fmtDate(new Date());
    setLoading(true);
    Promise.allSettled([
      axios.get('/api/v1/pfbs/recent', { params: { pfId: selectedPfId, d } }).then(r => setBs(arr(r.data))),
      axios.get('/api/v1/portfolio/positions', { params: { id: selectedPfId, d } }).then(r => setPositions(arr(r.data))),
      axios.get('/api/v1/portfolio/orders', { params: { id: selectedPfId, d0: d, d1: d } }).then(r => setOrders(arr(r.data))),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPfId]);

  // 관심종목(첫 그룹) + 상승률 상위
  useEffect(() => {
    (async () => {
      try {
        const groups = arr((await axios.get('/api/v1/watchlist/groups')).data);
        if (!groups.length) return;
        const items = arr((await axios.get(`/api/v1/watchlist/groups/${groups[0].id}/items`)).data);
        const ids: string[] = items.map((i: any) => i.prodId).filter((id: string) => /^[0-9Q]/.test(id));
        if (!ids.length) return;
        const qs = ids.map(id => `prodIds=${encodeURIComponent(id)}`).join('&');
        const nmRes = (await axios.get(`/api/v1/stocks/prod-nms?${qs}`)).data;
        const nms: Record<string, string> = nmRes?.data ?? nmRes ?? {};
        setProdId(p => p || ids[0]);
        const rows = await Promise.all(ids.map(async id => {
          const w: WatchRow = { prodId: id, prodNm: nms[id] || id };
          try {
            const h = arr((await axios.get('/api/v1/quant/screener/history', { params: { prodId: id } })).data);
            const n = h.length;
            if (n >= 2) { w.last = h[n - 1].c; w.chg = h[n - 2].c ? (h[n - 1].c / h[n - 2].c - 1) * 100 : 0; }
          } catch { /* 가격 없이 표시 */ }
          return w;
        }));
        setWatch(rows);
      } catch { setWatch([]); }
    })();
    axios.get('/api/v1/stocks/top-rise-rate', { params: { d: fmtDate(new Date()) } })
      .then(r => setRise(arr(r.data).slice(0, 8))).catch(() => setRise([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 메인 차트
  useEffect(() => {
    if (!prodId) return;
    (async () => {
      try {
        if (period === '5m') {
          const r = await axios.get('/api/v1/quant/intraday-bars', { params: { prodId, from: daysAgo(7), to: fmtDate(new Date()), minute: 5 } });
          setBars(arr(r.data).map((b: any) => ({ time: kstEpoch(String(b.d), String(b.t)), open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v })));
        } else {
          const r = await axios.get('/api/v1/quant/screener/history', { params: { prodId } });
          setBars(arr(r.data).map((b: any) => ({ time: b.date as Time, open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v })));
        }
      } catch { setBars([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodId, period]);

  // ── 파생값 ──
  const bsSorted = useMemo(() => [...bs].sort((a, b) => String(a.d).localeCompare(String(b.d))), [bs]);
  const today = bsSorted[bsSorted.length - 1];
  const yday = bsSorted[bsSorted.length - 2];
  const totAmt = today?.totEvluAmt;
  const dayDiff = today && yday ? today.totEvluAmt - yday.totEvluAmt : today?.diffAmt;
  const dayDiffPct = today && yday?.totEvluAmt ? (today.totEvluAmt / yday.totEvluAmt - 1) * 100 : undefined;
  const held = positions.filter(p => p.pos);
  const evalSum = held.reduce((s, p) => s + (p.evluamt ?? 0), 0);
  const prftSum = held.reduce((s, p) => s + (p.prft ?? 0), 0);
  const doneCnt = orders.filter(o => o.state === 30 && !o.errMsg && o.cumQty).length;
  const navBars: Bar[] = bsSorted.filter(b => b.totEvluAmt).map(b => ({
    time: String(b.d).slice(0, 10) as Time, open: b.totEvluAmt, high: b.totEvluAmt, low: b.totEvluAmt, close: b.totEvluAmt, volume: 0,
  }));
  const cur = watch.find(w => w.prodId === prodId);
  const maxAbsChg = Math.max(1, ...watch.map(w => Math.abs(w.chg ?? 0)));

  // ── 스타일 (토큰만 사용) ──
  const tile: React.CSSProperties = {
    background: t.tileBg, border: `1px solid ${t.tileBorder}`, borderRadius: 16, padding: 16,
    boxShadow: dark ? 'none' : '0 1px 2px rgba(30,64,175,0.06)', minWidth: 0, display: 'flex', flexDirection: 'column',
  };
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: t.title, marginBottom: 8 };
  const big: React.CSSProperties = { fontSize: 24, fontWeight: 700, color: t.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 };
  const sub: React.CSSProperties = { fontSize: 12, color: t.muted, marginTop: 4, fontVariantNumeric: 'tabular-nums' };
  const empty: React.CSSProperties = { fontSize: 12, color: t.hint, padding: '12px 0' };
  const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderTop: `1px solid ${t.divider}`, fontSize: 12, color: t.text, fontVariantNumeric: 'tabular-nums' };
  const chip = (on: boolean): React.CSSProperties => ({
    fontSize: 12, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
    border: `1px solid ${on ? t.accent : t.tileBorder}`, background: on ? t.accent : t.chip, color: on ? '#ffffff' : t.text,
  });

  const Kpi = ({ title, value, subText, color }: { title: string; value: string; subText?: React.ReactNode; color?: string }) => (
    <div className="t3" style={tile}>
      <div style={label}>{title}</div>
      <div style={{ ...big, color: color ?? t.text }}>{value}</div>
      {subText != null && <div style={sub}>{subText}</div>}
    </div>
  );

  return (
    <div style={{ padding: 20, background: t.pageBg, minHeight: '100vh', transition: 'background .2s' }}>
      <style>{LAYOUT_CSS}</style>

      {/* 머리 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.title }}>투자 대시보드 <span style={{ fontSize: 12, fontWeight: 400, color: t.hint }}>벤토 레이아웃 샘플</span></div>
          <div style={{ fontSize: 12, color: t.muted }}>계좌 {selectedPfId ?? '-'} · {fmtDate(new Date())} {loading ? '· 조회 중...' : ''}</div>
        </div>
        <button className="bento-btn" style={chip(dark)} onClick={toggleDark} aria-pressed={dark}>{dark ? '☾ 다크' : '☀ 라이트'}</button>
      </div>

      <div className="bento">
        {/* KPI */}
        <Kpi title="총 평가금액" value={totAmt != null ? `${won(totAmt)}원` : '-'}
          subText={<span style={{ color: bentoSign(t, dayDiff) }}>전일 대비 {won(dayDiff)} ({pct(dayDiffPct)})</span>} />
        <Kpi title="오늘 실현손익" value={today?.tradeNet != null ? `${won(today.tradeNet)}원` : '-'} color={bentoSign(t, today?.tradeNet)}
          subText={today?.tradeFlat ? '당일 전량 청산' : '비용 차감 후'} />
        <Kpi title="보유 평가손익" value={`${won(prftSum)}원`} color={bentoSign(t, prftSum)}
          subText={`보유 ${held.length}종목 · 평가 ${eok(evalSum)}`} />
        <Kpi title="오늘 주문 / 체결" value={`${orders.length} / ${doneCnt}`}
          subText={`거부 ${orders.filter(o => o.errMsg).length} · 진행 ${orders.filter(o => o.state !== 30 && !o.errMsg && !o.cnclT).length}`} />

        {/* 메인 차트 */}
        <div className="t8" style={tile}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={label}>{cur?.prodNm ?? prodId} <span style={{ color: t.hint, fontWeight: 400 }}>{prodId}</span></div>
              {cur?.last != null && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ ...big, fontSize: 20, color: bentoSign(t, cur.chg) }}>{won(cur.last)}</span>
                  <span style={{ fontSize: 12, color: bentoSign(t, cur.chg) }}>{pct(cur.chg)}</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="bento-btn" style={chip(period === '5m')} onClick={() => setPeriod('5m')}>5분</button>
              <button className="bento-btn" style={chip(period === 'D')} onClick={() => setPeriod('D')}>일</button>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 360 }}>
            {bars.length ? <TvChart bars={bars} height={380} intraday={period === '5m'} theme={chartTheme} />
              : <div style={empty}>차트 데이터가 없습니다</div>}
          </div>
        </div>

        {/* 관심종목 히트맵 */}
        <div className="t4" style={tile}>
          <div style={label}>관심종목 <span style={{ color: t.hint, fontWeight: 400 }}>(클릭: 차트)</span></div>
          {watch.length === 0 ? <div style={empty}>관심종목이 없습니다</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
              {watch.map(w => {
                const k = Math.min(1, Math.abs(w.chg ?? 0) / maxAbsChg);
                const base = (w.chg ?? 0) >= 0 ? (dark ? '248,113,113' : '220,38,38') : (dark ? '96,165,250' : '37,99,235');
                const on = w.prodId === prodId;
                return (
                  <button key={w.prodId} className="bento-tile" onClick={() => setProdId(w.prodId)}
                    style={{
                      textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '8px 10px',
                      background: `rgba(${base},${0.08 + k * 0.32})`,
                      border: `1px solid ${on ? t.accent : 'transparent'}`, color: t.text,
                    }}>
                    <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.prodNm}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: bentoSign(t, w.chg) }}>{pct(w.chg)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 보유 비중 */}
        <div className="t4" style={tile}>
          <div style={label}>보유 비중 <span style={{ color: t.hint, fontWeight: 400 }}>(평가금액 기준)</span></div>
          {held.length === 0 ? <div style={empty}>보유 종목이 없습니다</div> : (
            [...held].sort((a, b) => (b.evluamt ?? 0) - (a.evluamt ?? 0)).slice(0, 6).map(p => {
              const w = evalSum ? (p.evluamt ?? 0) / evalSum * 100 : 0;
              return (
                <div key={p.prodid ?? p.prodId} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: t.text, marginBottom: 4 }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.prodNm ?? p.prodid}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: t.muted }}>{w.toFixed(1)}% · <span style={{ color: bentoSign(t, p.prft) }}>{won(p.prft)}</span></span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: t.divider }}>
                    <div style={{ width: `${w}%`, height: '100%', borderRadius: 3, background: t.accent }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* 자산 추이 */}
        <div className="t4" style={tile}>
          <div style={label}>자산 추이 <span style={{ color: t.hint, fontWeight: 400 }}>(총 평가금액)</span></div>
          {navBars.length < 2 ? <div style={empty}>잔고 이력 {navBars.length}일 — 2일 이상 쌓이면 그립니다</div>
            : <TvChart bars={navBars} height={200} mode="area" theme={chartTheme} lineColor={t.accent} />}
        </div>

        {/* 상승률 상위 */}
        <div className="t4" style={tile}>
          <div style={label}>상승률 상위 <span style={{ color: t.hint, fontWeight: 400 }}>(오늘)</span></div>
          {rise.length === 0 ? <div style={empty}>오늘 시세가 아직 없습니다</div> : rise.map((r, i) => (
            <div key={r.prodId} className="bento-row" style={{ ...row, cursor: 'pointer' }} onClick={() => setProdId(r.prodId)}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ color: t.hint, display: 'inline-block', width: 18 }}>{i + 1}</span>{r.prodNm ?? r.prodId}
              </span>
              <span style={{ flexShrink: 0 }}>{won(r.clsprc)} <b style={{ color: bentoSign(t, r.flurt) }}>{pct(r.flurt)}</b></span>
            </div>
          ))}
        </div>

        {/* 오늘 주문 */}
        <div className="t4" style={tile}>
          <div style={label}>오늘 주문 <span style={{ color: t.hint, fontWeight: 400 }}>(최근 8건)</span></div>
          {orders.length === 0 ? <div style={empty}>오늘 주문이 없습니다</div> : [...orders].reverse().slice(0, 8).map(o => {
            const buy = (o.ls ?? 1) * (o.ex ?? 1) > 0;   // sgn = ls×ex
            const st = o.errMsg ? '거부' : o.state === 30 ? (o.cumQty ? '완료' : '미체결') : o.cnclT ? '취소' : '진행';
            return (
              <div key={o.id} style={row}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, marginRight: 6,
                    color: buy ? t.up : t.down, background: buy ? t.upBg : t.downBg }}>{buy ? '매수' : '매도'}</span>
                  {o.prodNm ?? o.prodId}
                </span>
                <span style={{ flexShrink: 0, color: t.muted }}>{won(o.cumQty)}/{won(o.qty2ord ?? o.qty2Ord)} · <span style={{ color: o.errMsg ? t.up : t.muted }}>{st}</span></span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BentoDashboard;
